import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import type { ListingStatsModel } from '../../../api/types/admin';

export type ListingStatsChartMetric =
  | 'statsByTopCategories'
  | 'statsBySecondaryCategories'
  | 'statsByProductTypes'
  | 'statsByStatuses'
  | 'statsByCities'
  | 'statsByUsers';

const COLORS = ['#0d6efd', '#198754', '#fd7e14', '#6f42c1', '#20c997', '#e83e8c', '#6c757d'];

const STATUS_COLORS: Record<string, string> = {
  pending: '#0d6efd',
  active: '#198754',
  rejected: '#dc3545',
};

function resolveStatusColor(name: string | undefined | null): string | undefined {
  if (!name) return undefined;
  const n = name.toLowerCase();
  if (n.includes('bekle') || n === 'pending') return STATUS_COLORS.pending;
  if (n.includes('aktif') || n === 'active') return STATUS_COLORS.active;
  if (n.includes('red') || n === 'rejected') return STATUS_COLORS.rejected;
  return undefined;
}

const metricLabels: Record<ListingStatsChartMetric, string> = {
  statsByTopCategories: 'Üst Kategori',
  statsBySecondaryCategories: 'Alt Kategori',
  statsByProductTypes: 'Ürün Tipi',
  statsByStatuses: 'Durum',
  statsByCities: 'Şehir',
  statsByUsers: 'Kullanıcı',
};

type DataItem = { id: number; name: string; count: number; ratio: number };

function getDataForMetric(
  stats: ListingStatsModel,
  metric: ListingStatsChartMetric
): DataItem[] {
  const arr = stats[metric];
  if (!Array.isArray(arr) || arr.length === 0) return [];
  return arr.map((x) => ({
    id: x.id,
    name: x.name ?? `#${x.id}`,
    count: x.count,
    ratio: x.ratio,
  }));
}

type ListingStatsChartViewProps = {
  stats: ListingStatsModel | null;
  metric: ListingStatsChartMetric;
};

const ListingStatsChartView: React.FC<ListingStatsChartViewProps> = ({ stats, metric }) => {
  if (!stats) {
    return (
      <div className="d-flex align-items-center justify-content-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Yükleniyor...</span>
        </div>
      </div>
    );
  }

  const data = getDataForMetric(stats, metric);
  if (data.length === 0) {
    return (
      <div className="text-muted text-center py-4">
        Bu metrik için veri yok. Toplam ilan: {stats.totalListingCount}
      </div>
    );
  }

  const chartData = data.map((d, i) => ({
    ...d,
    color: (metric === 'statsByStatuses' ? resolveStatusColor(d.name) : undefined)
      ?? COLORS[i % COLORS.length],
  }));

  return (
    <ResponsiveContainer width="100%" height={300}>
      <PieChart>
        <Pie
          data={chartData}
          cx="50%"
          cy="50%"
          innerRadius={60}
          outerRadius={100}
          paddingAngle={2}
          dataKey="count"
          nameKey="name"
        >
          {chartData.map((entry, index) => (
            <Cell key={`cell-${entry.id}-${index}`} fill={entry.color} />
          ))}
        </Pie>
        <Tooltip formatter={(value: number | undefined) => [value ?? 0, 'Adet']} />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  );
};

export { metricLabels };
export default ListingStatsChartView;
